import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import {
  Router,
  RouterLink,
  RouterLinkActive,
  RouterOutlet,
} from '@angular/router';
import { ToastHost } from './toast-host';
import { NavBadgesStore } from './nav-badges.store';
import { AuthService } from '../auth/auth.service';
import { CvthequeStore } from '../../features/cvtheque/data/cvtheque.store';
import { EntretiensStore } from '../../features/entretiens/data/entretiens.store';
import { MatchingStore } from '../../features/matching/data/matching.store';
import { NotificationsStore } from '../../features/notifications/data/notifications.store';
import { ThemeService } from '../services/theme.service';

interface NavItem {
  label: string;
  path: string;
  icon: string;
  badge?: number;
  badgeAccent?: boolean;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

@Component({
  selector: 'app-shell',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, ToastHost],
  templateUrl: './shell.html',
  styleUrl: './shell.scss',
})
export class Shell {
  protected readonly theme = inject(ThemeService);
  protected readonly auth = inject(AuthService);
  protected readonly notifications = inject(NotificationsStore);
  protected readonly navBadges = inject(NavBadgesStore);
  private readonly router = inject(Router);
  private cvtheque = inject(CvthequeStore);
  private matching = inject(MatchingStore);
  private entretiens = inject(EntretiensStore);

  protected readonly bellOpen = signal(false);
  protected readonly userMenuOpen = signal(false);
  protected readonly isAdmin = computed(() => this.auth.isAdmin());

  protected userInitials(): string {
    const name = this.auth.userDisplayName();
    return name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('');
  }

  protected search(term: string): void {
    const q = term.trim();
    if (!q) {
      return;
    }
    this.router.navigate(['/offres'], {
      queryParams: { q, view: 'by-offer' },
    });
  }

  protected toggleBell(): void {
    this.bellOpen.update((v) => !v);
    this.userMenuOpen.set(false);
  }

  protected toggleUserMenu(): void {
    this.userMenuOpen.update((v) => !v);
    this.bellOpen.set(false);
  }

  protected closeMenus(): void {
    this.bellOpen.set(false);
    this.userMenuOpen.set(false);
  }

  protected timeAgo(iso: string): string {
    const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
    if (min < 1) return "À l'instant";
    if (min < 60) return `Il y a ${min} min`;
    const h = Math.round(min / 60);
    if (h < 24) return `Il y a ${h} h`;
    return `Il y a ${Math.round(h / 24)} j`;
  }

  protected readonly navGroups = computed<NavGroup[]>(() => {
    const groups: NavGroup[] = [];

    groups.push({
      items: [{ label: 'Tableau de bord', path: 'dashboard', icon: '📊' }],
    });

    const gestionItems: NavItem[] = [
      {
        label: 'CVthèque',
        path: 'cvtheque',
        icon: '📄',
        badge: this.cvtheque.cvEnAttenteCount(),
      },
      {
        label: 'Entretiens',
        path: 'entretiens',
        icon: '📅',
        badge: this.entretiens.totalCount(),
        badgeAccent: true,
      },
      {
        label: 'Entreprises',
        path: 'entreprises',
        icon: '🏢',
        badge: this.navBadges.entreprisesEnDiscussion(),
      },
      {
        label: 'Offres',
        path: 'offres',
        icon: '💼',
        badge: this.navBadges.offresAvecCandidats(),
      },
      {
        label: 'Matching',
        path: 'matching',
        icon: '🔗',
        badge: this.matching.inProgressCount(),
      },
    ];

    groups.push({ label: 'Gestion', items: gestionItems });

    return groups;
  });

  protected readonly bottomNav = computed<NavItem[]>(() => [
    { label: 'Profil', path: 'profile', icon: '👤' },
    { label: 'Notifications', path: 'notifications', icon: '🔔' },
    { label: 'Support', path: 'support', icon: '🛟' },
  ]);
}
